import { useState, useEffect } from 'react'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { db } from '../../lib/firebase'
import './AdminEditor.css'

export default function UserWhitelist() {
  const [emails, setEmails] = useState([])
  const [newEmail, setNewEmail] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)

  // Load current whitelist
  useEffect(() => {
    loadWhitelist()
  }, [])

  const loadWhitelist = async () => {
    try {
      setLoading(true)
      const docRef = doc(db, 'config', 'allowedUsers')
      const snapshot = await getDoc(docRef)

      if (snapshot.exists()) {
        setEmails(snapshot.data().emails || [])
      } else {
        setEmails([])
      }
    } catch (err) {
      setError('Failed to load whitelist: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  const saveWhitelist = async (newEmails) => {
    setSaving(true)
    setError(null)
    setSuccess(null)

    try {
      const docRef = doc(db, 'config', 'allowedUsers')
      await setDoc(docRef, { emails: newEmails })
      setEmails(newEmails)
      setSuccess('Whitelist updated successfully')
      setTimeout(() => setSuccess(null), 3000)
    } catch (err) {
      setError('Failed to save: ' + err.message)
    } finally {
      setSaving(false)
    }
  }

  const handleAddEmail = async (e) => {
    e.preventDefault()

    const email = newEmail.trim().toLowerCase()

    if (!email) return

    // Validate email format
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address')
      return
    }

    // Check for duplicates
    if (emails.includes(email)) {
      setError('This email is already in the whitelist')
      return
    }

    const newEmails = [...emails, email].sort()
    await saveWhitelist(newEmails)
    setNewEmail('')
  }

  const handleRemoveEmail = async (emailToRemove) => {
    if (!window.confirm(`Remove ${emailToRemove} from the whitelist?`)) {
      return
    }

    const newEmails = emails.filter(e => e !== emailToRemove)
    await saveWhitelist(newEmails)
  }

  if (loading) {
    return (
      <div className="container">
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <div className="container">
      <div className="editor-header">
        <h1>User Whitelist</h1>
      </div>

      <p className="text-secondary mb-lg">
        Only users with whitelisted email addresses can access this site.
        The site owner (shop@peloingole.com) always has access.
      </p>

      {error && <div className="error-banner mb-lg">{error}</div>}
      {success && <div className="success-banner mb-lg">{success}</div>}

      <form onSubmit={handleAddEmail} className="whitelist-form">
        <div className="form-group" style={{ flex: 1 }}>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="player@email.com"
            disabled={saving}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving || !newEmail.trim()}
        >
          {saving ? 'Adding...' : 'Add Email'}
        </button>
      </form>

      <div className="whitelist-section">
        <h3>Whitelisted Users ({emails.length})</h3>

        {emails.length === 0 ? (
          <p className="text-secondary">No users whitelisted yet. Add some player emails above.</p>
        ) : (
          <div className="whitelist-list">
            {emails.map(email => (
              <div key={email} className="whitelist-item">
                <span className="whitelist-email">{email}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleRemoveEmail(email)}
                  disabled={saving}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
